import { Injectable, inject, signal } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import {
  AcceptInvitationResponse,
  PendingStoreInvitation,
  RevokeStoreShareResponse,
  ShareStorePayload,
  StoreConfigResponse,
  StoreCreateDTO,
  StoreDeliveryPayload,
  StoreGetDTO,
  StoreInvitation,
  StoreThresholdsPayload,
  StoreUpdateDTO,
} from "src/app/pages/store-management/stores/store";
import { SessionService } from "../../session.service";
import { BaseCrudService } from "../generic/base.service";
import { NoShowToastContext, ToastContext } from "src/app/core/interceptors/http-toast.interceptor";
import {
  catchError,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from "rxjs";
import { environment } from "src/environments/environment";
import { ToastService } from "src/app/shared/services/toast.service";
import { TranslateService } from "@ngx-translate/core";
import { coerceApiUnixTimestampToIso } from "src/app/shared/helpers/api-timestamp.helper";

@Injectable({
  providedIn: "root",
})
export class StoreService extends BaseCrudService<
  StoreGetDTO[],
  StoreCreateDTO,
  StoreUpdateDTO,
  string
> {
  private baseUrl = environment.baseApiUrl;
  private toast = inject(ToastService);
  private translate = inject(TranslateService);

  constructor() {
    super("/entity/user_store"); // ✅

    //this.delete("b274a7d8-6f42-4d9c-b18a-07a66886ce0f").subscribe();
  }

  stores = signal<StoreGetDTO[]>([]);
  /** True after a successful list fetch (including an empty store list). */
  private storesListLoaded = signal(false);
  storeConfigs = signal<Record<string, StoreConfigResponse>>({});

  /** Shared in-flight list request so parallel callers only hit the API once. */
  private storesListLoad$: Observable<StoreGetDTO[]> | null = null;

  /** In-flight store config requests keyed by storeId. */
  private readonly storeConfigLoads = new Map<
    string,
    Observable<StoreConfigResponse>
  >();

  private appendMockBillingStore(stores: StoreGetDTO[]): StoreGetDTO[] {
    if (!environment.mockExtraBillingStore || !stores.length) {
      return stores;
    }

    const [firstStore] = stores;
    const mockStoreId = `${firstStore.storeId}-billing-preview`;

    if (stores.some((store) => store.storeId === mockStoreId)) {
      return stores;
    }

    return [
      ...stores,
      {
        ...firstStore,
        storeId: mockStoreId,
        name: `${firstStore.name} (Preview)`,
        providerStoreId: `${firstStore.providerStoreId}-preview`,
      },
    ];
  }

  /** Stores with completed onboarding — use for filters and default store selection. */
  static getCompletedStores(stores: StoreGetDTO[]): StoreGetDTO[] {
    return stores.filter((store) => store.onboarding?.isComplete === true);
  }

  /** Applies the single completed-store default when no storeId is selected. */
  static resolveStoreIdWithSingleStoreDefault(
    storeId: string,
    stores: StoreGetDTO[],
    applySingleStoreDefault: boolean,
  ): string {
    if (storeId) {
      return storeId;
    }

    if (!applySingleStoreDefault) {
      return "";
    }

    const completedStores = StoreService.getCompletedStores(stores);
    return completedStores.length === 1
      ? (completedStores[0]?.storeId ?? "")
      : "";
  }

  /** Store id for claim-view resolver initial API calls. */
  static resolveInitialClaimViewStoreId(
    stores: StoreGetDTO[],
    queryStoreId?: string,
  ): string {
    const resolved = StoreService.resolveStoreIdWithSingleStoreDefault(
      queryStoreId ?? "",
      stores,
      true,
    );

    if (resolved) {
      return resolved;
    }

    return StoreService.getCompletedStores(stores)[0]?.storeId ?? "";
  }

  clearCache(): void {
    this.stores.set([]);
    this.storesListLoaded.set(false);
    this.storeConfigs.set({});
    this.storesListLoad$ = null;
    this.storeConfigLoads.clear();
  }

  /** Returns cached stores when available; otherwise loads from API. */
  getCachedOrLoad(): Observable<StoreGetDTO[]> {
    if (this.storesListLoaded()) {
      return of(this.stores());
    }

    if (!this.storesListLoad$) {
      this.storesListLoad$ = this.get().pipe(
        shareReplay(1),
        finalize(() => {
          this.storesListLoad$ = null;
        }),
      );
    }

    return this.storesListLoad$;
  }

  getCachedStoreConfig(storeId: string): StoreConfigResponse | null {
    return this.storeConfigs()[storeId] ?? null;
  }

  /** Always loads the store config from the API; parallel callers share one in-flight request. */
  loadStoreConfig(storeId: string): Observable<StoreConfigResponse> {
    let pending = this.storeConfigLoads.get(storeId);
    if (!pending) {
      pending = this.fetchStoreConfig(storeId).pipe(
        shareReplay(1),
        finalize(() => {
          this.storeConfigLoads.delete(storeId);
        }),
      );
      this.storeConfigLoads.set(storeId, pending);
    }

    return pending;
  }

  /** Loads configs for all cached stores missing from the config map (optional bulk). */
  ensureStoreConfigsCached(): Observable<void> {
    const stores = this.stores();
    if (!stores.length) {
      return of(undefined);
    }

    const cached = this.storeConfigs();
    const missingStoreIds = stores
      .map((store) => store.storeId)
      .filter((storeId) => storeId && !cached[storeId]);

    if (!missingStoreIds.length) {
      return of(undefined);
    }

    return forkJoin(
      missingStoreIds.map((storeId) =>
        this.fetchStoreConfig(storeId, true).pipe(
          catchError(() => of(null)),
        ),
      ),
    ).pipe(map(() => undefined));
  }

  /** Returns the list of pending store-sharing invitations for the current user. */
  getPendingInvitations(): Observable<PendingStoreInvitation[]> {
    const url = `${this.baseUrl}/entity/user_store/pending-invitations`;
    return this.http.get<PendingStoreInvitation[]>(url, NoShowToastContext()).pipe(
      catchError((error: unknown) => {
        // An unverified email is an expected state (shown as a hint), not a failure.
        const errorType = (error as { error?: { errorType?: string } })?.error?.errorType;
        if (errorType !== "emailNotVerified") {
          this.toast.error(
            this.translate.instant("toast_errors.getPendingInvitationsFailed"),
          );
        }
        return throwError(() => error);
      }),
    );
  }

  /**
   * Sends a store-sharing invitation. Returns the full HttpResponse so callers
   * can distinguish 201 (new invitation) from 200 (already pending).
   */
  shareStore(storeId: string, payload: ShareStorePayload): Observable<import("@angular/common/http").HttpResponse<{ invitation: StoreInvitation }>> {
    const url = `${this.baseUrl}/entity/user_store/${storeId}/share`;
    return this.http.post<{ invitation: StoreInvitation }>(url, payload, {
      ...NoShowToastContext(),
      observe: "response",
    });
  }

  /** Lists pending outgoing store-share invitations for the given store (owner only). */
  getStoreInvitations(storeId: string): Observable<StoreInvitation[]> {
    const url = `${this.baseUrl}/entity/user_store/${storeId}/invitations`;
    return this.http.get<StoreInvitation[]>(url, NoShowToastContext());
  }

  /**
   * Revokes store share access for one or more users (pending invitations or active).
   * Returns the raw response; callers must check `removed` and `cancelledInvitations` before showing success.
   */
  revokeStoreShare(storeId: string, emails: string[]): Observable<RevokeStoreShareResponse> {
    const url = `${this.baseUrl}/entity/user_store/${storeId}/share`;
    return this.http.delete<RevokeStoreShareResponse>(url, { ...NoShowToastContext(), body: { emails } }).pipe(
      // Only active shares change store data (sharedWith); cancelled pending invitations don't.
      // Refresh in place (no clearCache) so the sidenav never sees an empty stores list.
      switchMap((res) =>
        res.removed?.length ? this.get().pipe(map(() => res)) : of(res),
      ),
    );
  }

  /** Accepts a pending store invitation, linking the shared store to the current user. */
  acceptInvitation(storeId: string): Observable<AcceptInvitationResponse> {
    const url = `${this.baseUrl}/entity/user_store/${storeId}/accept-invitation`;
    return this.http.post<AcceptInvitationResponse>(url, {}, NoShowToastContext()).pipe(
      switchMap((res) => this.get().pipe(map(() => res))),
    );
  }

  initLinkStoreUrl = `/store-link/{provider}/authorize`;

  initLinkStore(
    provider: string,
    params?: any,
  ): Observable<{ returnUrl: string }> {
    const url =
      environment.baseApiUrl +
      this.initLinkStoreUrl.replace("{provider}", provider);
    return this.http.get<{ returnUrl: string }>(url, { params: params }).pipe(
      catchError((error: any) => {
        this.toast.error(
          this.translate.instant("toast_errors.initLinkStoreFailed"),
        );
        return throwError(() => error);
      }),
    );
  }

  /**
   * Same-tab redirect for every provider, including Amazon — a stale Treeslo
   * tab left open from before the store got linked can keep its old
   * WebSocket connection around and swallow onboarding events meant for the
   * tab the user actually returns to.
   */
  openStoreLinkReturnUrl(returnUrl: string): void {
    window.location.href = returnUrl;
  }

  updateDelivery(
    storeId: string,
    delivery: StoreDeliveryPayload,
  ): Observable<{ message: string }> {
    const url = `${environment.baseApiUrl}/stores/config/delivery`;
    const body = {
      storeId,
      ...delivery,
    };

    return this.http.put<{ message: string }>(url, body).pipe(
      switchMap((response) =>
        this.getStoreConfig(storeId).pipe(
          switchMap(() => this.get()),
          map(() => response),
        ),
      ),
      catchError((error: any) => {
        this.toast.error(
          this.translate.instant("toast_errors.updateDeliveryFailed"),
        );
        return throwError(() => error);
      }),
    );
  }

  updateThreshold(
    storeId: string,
    thresholds: StoreThresholdsPayload,
  ): Observable<{ message: string }> {
    const url = `${environment.baseApiUrl}/stores/config/thresholds`;
    const body = {
      storeId,
      thresholds,
    };

    return this.http.put<{ message: string }>(url, body).pipe(
      switchMap((response) =>
        this.getStoreConfig(storeId).pipe(
          switchMap(() => this.get()),
          map(() => response),
        ),
      ),
      catchError((error: any) => {
        this.toast.error(
          this.translate.instant("toast_errors.updateThresholdFailed"),
        );
        return throwError(() => error);
      }),
    );
  }

  override update(
    id: string,
    data: StoreUpdateDTO,
    toastContext?: ToastContext,
  ): Observable<StoreGetDTO[]> {
    return super.update(id, data, toastContext).pipe(
      switchMap(() => this.get()),
      catchError((error: any) => {
        this.toast.error(
          this.translate.instant("toast_errors.updateStoreFailed"),
        );
        return throwError(() => error);
      }),
    );
  }
  override delete(
    id: string,
    toastContext?: ToastContext,
  ): Observable<StoreGetDTO[]> {
    return super.delete(id, toastContext).pipe(
      switchMap(() => this.get()),
      catchError((error: any) => {
        this.toast.error(
          this.translate.instant("toast_errors.deleteStoreFailed"),
        );
        return throwError(() => error);
      }),
    );
  }

  getStoreConfig(storeId: string): Observable<StoreConfigResponse> {
    return this.fetchStoreConfig(storeId);
  }

  private fetchStoreConfig(
    storeId: string,
    suppressErrorToast = false,
  ): Observable<StoreConfigResponse> {
    const url = `${environment.baseApiUrl}/stores/config`;
    return this.http
      .get<StoreConfigResponse>(url, { params: { storeId } })
      .pipe(
        tap((config) => this.setStoreConfigCache(storeId, config)),
        catchError((error: unknown) => {
          if (!suppressErrorToast) {
            this.toast.error(
              this.translate.instant("toast_errors.getStoreConfigFailed"),
            );
          }
          return throwError(() => error);
        }),
      );
  }

  private setStoreConfigCache(
    storeId: string,
    config: StoreConfigResponse,
  ): void {
    this.storeConfigs.update((current) => ({
      ...current,
      [storeId]: config,
    }));
  }

  private normalizeStore(store: StoreGetDTO): StoreGetDTO {
    const linkedAt = coerceApiUnixTimestampToIso(
      store.linkedAt as string | number,
    );
    const normalized: StoreGetDTO = linkedAt ? { ...store, linkedAt } : store;

    // Delegated stores skip the onboarding flow — always treat as complete.
    if (normalized.isBillingDelegated && !normalized.onboarding?.isComplete) {
      return {
        ...normalized,
        onboarding: {
          isComplete: true,
          missingDeliveryData: false,
          missingThresholdData: false,
        },
      };
    }

    return normalized;
  }

  private normalizeStores(stores: StoreGetDTO[]): StoreGetDTO[] {
    return stores.map((store) => this.normalizeStore(store));
  }

  private pruneStoreConfigCache(stores: StoreGetDTO[]): void {
    const validStoreIds = new Set(stores.map((store) => store.storeId));

    this.storeConfigs.update((current) =>
      Object.fromEntries(
        Object.entries(current).filter(([storeId]) =>
          validStoreIds.has(storeId),
        ),
      ),
    );
  }

  override get(): Observable<StoreGetDTO[]>;
  override get(id: string): Observable<StoreGetDTO>;
  override get(id?: string): Observable<StoreGetDTO[] | StoreGetDTO> {
    if (id) {
      return super.get(id as string).pipe(
        map((response) => {
          if (Array.isArray(response)) {
            const foundStore = response.find((item) => item.storeId === id);
            if (!foundStore) {
              throw new HttpErrorResponse({
                status: 404,
                statusText: "Not Found",
                url: `${environment.baseApiUrl}${this.endpoint}${id}`,
                error: { message: `Store with id ${id} not found` },
              });
            }
            return this.normalizeStore(foundStore as StoreGetDTO);
          }
          return this.normalizeStore(response as StoreGetDTO);
        }),
      ) as unknown as Observable<StoreGetDTO>;
    }

    return super.get().pipe(
      map((stores) => this.normalizeStores(stores as StoreGetDTO[])),
      // map((response) =>
      //   (response as StoreGetDTO[]).map((r) => ({
      //     ...r,
      //     onboarding: {
      //       isComplete: false,
      //       missingDeliveryData: r.onboarding?.missingDeliveryData ?? false,
      //       missingThresholdData: r.onboarding?.missingThresholdData ?? false,
      //     },
      //   })),
      // ),
      //map((response) => []),
      tap((stores) => {
        const nextStores = this.appendMockBillingStore(stores);
        this.stores.set(nextStores);
        this.storesListLoaded.set(true);
        this.pruneStoreConfigCache(nextStores);
      }),
      catchError((error: any) => {
        this.toast.error(
          this.translate.instant("toast_errors.getStoresFailed"),
        );
        return throwError(() => error);
      }),
    );
  }
}
